/**
 * Verificação dos horários sobre a agenda REAL.
 *
 * A barreira final (`schedule-validation`) trabalha num relógio idealizado: não
 * conhece os blocos de refeição nem a logística que `buildSchedule` intercala.
 * Por isso uma visita podia acabar agendada às 20:44 num museu que fecha às
 * 18:00. Este módulo recebe as entradas já temporizadas e diz quais são as
 * visitas opcionais que caem fora do horário real.
 *
 * Módulo puro: sem IO, sem React, sem relógio.
 */

import { periodsToDaySlots, type OpeningPeriod } from "./place-availability";
import { conservativeEndCap, type ValidatableStop } from "./schedule-validation";

export type TimedStopLike = {
  type: "stop" | "break";
  idx?: number;
  arriveAt?: Date;
  leaveAt?: Date;
};

export type HoursCheckStop = {
  openingPeriods?: OpeningPeriod[] | null;
  required?: boolean;
  placeTypes?: string[];
  placePrimaryType?: string;
  category?: string;
  name?: string;
};

/** Sobreposição mínima com o horário de abertura para a visita valer a pena. */
const MIN_OPEN_OVERLAP = 20;

function fitsOpeningHours(
  periods: OpeningPeriod[] | null | undefined,
  arrive: Date,
  leave: Date,
): boolean {
  const slots = periodsToDaySlots(periods);
  if (!slots.length) return true; // horários desconhecidos: outra regra trata disto
  const weekday = arrive.getDay();
  const from = arrive.getHours() * 60 + arrive.getMinutes();
  const to = from + Math.max(5, (leave.getTime() - arrive.getTime()) / 60_000);
  const need = Math.min(to - from, MIN_OPEN_OVERLAP);
  return slots.some(
    (s) => s.day === weekday && Math.min(s.endMinutes, to) - Math.max(s.startMinutes, from) >= need,
  );
}

/**
 * Índices (`idx` das entradas do tipo "stop") das visitas opcionais agendadas
 * fora do horário real ou depois do limite conservador de um interior sem
 * horários conhecidos.
 */
export function findStopsOutsideOpeningHours(
  entries: readonly TimedStopLike[],
  stops: readonly HoursCheckStop[],
): number[] {
  const out: number[] = [];
  for (const entry of entries) {
    if (entry.type !== "stop" || entry.idx == null) continue;
    const stop = stops[entry.idx];
    if (!stop || stop.required) continue;
    const arrive = entry.arriveAt;
    const leave = entry.leaveAt;
    if (!(arrive instanceof Date) || !(leave instanceof Date)) continue;
    if (stop.openingPeriods?.length) {
      if (!fitsOpeningHours(stop.openingPeriods, arrive, leave)) out.push(entry.idx);
      continue;
    }
    // Sem horários confirmados: interiores nunca ficam agendados à noite.
    const cap = conservativeEndCap({
      id: "guard",
      day: 1,
      openingPeriods: null,
      placeTypes: stop.placeTypes,
      placePrimaryType: stop.placePrimaryType,
      category: stop.category,
      name: stop.name,
    } as ValidatableStop);
    if (cap == null) continue;
    const endMinutes = leave.getHours() * 60 + leave.getMinutes();
    if (endMinutes > cap) out.push(entry.idx);
  }
  return out;
}
